<script setup lang="ts">
import type {HTMLAttributes} from 'vue'
import {useSlots} from 'vue'

import type {PrimitiveProps} from 'radix-vue'
import {Primitive} from 'radix-vue'
import {type ButtonVariants, buttonVariants} from '.'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {IconDefinition} from '@fortawesome/fontawesome-svg-core'
import {cn} from '@/shared/lib/utils'
import {faSpinner} from '@fortawesome/pro-solid-svg-icons'
import {hasVisibleContent} from "@/shared/composable/utils.ts";

interface Props extends PrimitiveProps {
  variant?: ButtonVariants['variant']
  size?: ButtonVariants['size']
  icon?: IconDefinition
  busyIcon?: IconDefinition
  busy?: boolean
  disabled?: boolean
  as?: string
  class?: HTMLAttributes['class']
  iconClass?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
  as: 'button',
  busy: false,
  disabled: false,
  busyIcon: () => faSpinner,
})

const emit = defineEmits<{
  (e: 'click'): void
}>()

const onClick = () => {
  if(props.busy || props.disabled) return
  emit('click')
}

const slots = useSlots();

const hasContent = () => {
  const vnodes = slots.default ? slots.default() : null;
  return hasVisibleContent(vnodes);
};

</script>

<template>
  <Primitive
      @click="onClick"
      :as="as"
      :as-child="asChild"
      :disabled="busy || disabled"
      :class="cn(buttonVariants({ variant, size }), props.class)"
      type="button"
  >
    <FontAwesomeIcon
        style="--fa-secondary-opacity: 1;"
        v-if="busyIcon && busy"
        :icon="busyIcon"
        :class="{ 'mr-2': hasContent() }"
        :spin="true"
    />
    <FontAwesomeIcon
        v-if="icon && !busy"
        :icon="icon"
        :class="[{ 'mr-2': hasContent() }, iconClass]"
    />
    <slot/>
  </Primitive>
</template>